import { z } from 'zod';
/**
 * Rocks — 90-day priorities. Each Rock is a single, specific, measurable
 * goal owned by exactly one person and finished within the quarter.
 * Wickman's rule of thumb is 3–7 company Rocks per quarter and 3–7
 * personal Rocks per leadership-team seat. A Rock either gets done or
 * it doesn't; "off-track" is the early-warning state surfaced at the
 * weekly L10.
 *
 * Storage path: `rocks/{rockId}`. Reads gated by `orgId`. Milestones
 * are embedded — they're cheap, ordered, and always read alongside the
 * Rock.
 */
export const ROCKS_COLLECTION = 'rocks';
/**
 * Rock status. `on-track` and `off-track` are the live working states
 * surfaced during quarter; `done` and `not-done` are the terminal
 * states recorded at end-of-quarter scoring.
 */
export const RockStatus = z.enum(['on-track', 'off-track', 'done', 'not-done']);
/**
 * A milestone inside a Rock — the smaller checkpoints that prove the
 * Rock is moving. Embedded in the parent doc; `id` is generated
 * client-side (uuid) so the array can be reordered/edited without
 * subcollection writes.
 */
export const RockMilestone = z.object({
    id: z.string().min(1),
    title: z.string().min(1),
    /** ISO timestamp when the milestone was checked off; absent = open. */
    completedAt: z.string().optional(),
});
/**
 * Agent-written status update — synthesized from connector activity
 * (Slack mentions, GitHub PRs touching the relevant code, Salesforce
 * deals, etc.). The `cites` array carries the connector source labels
 * (`<SourceChip name="...">`) the UI renders beneath the body. INTERP
 * marks any sentence the model interpreted vs. quoted from the source.
 *
 * The whole block is optional — Rocks without connector data, or in
 * orgs that haven't wired any sources, render the card without the
 * agent-update strip. PR #34's `<RockCard>` accepts these fields as
 * separate props (update / cites / interp) so the card stays
 * presentational; this Zod object is just the wire-shape mirror.
 */
export const RockAgentUpdate = z.object({
    body: z.string().min(1),
    /** ISO timestamp when the synthesis ran. */
    generatedAt: z.string(),
    /** Model id (e.g. `gemini-3.1-flash-lite-preview`) or `mock` for the deterministic fallback. */
    model: z.string().min(1),
    /** Connector source labels — rendered as `<SourceChip name=...>` chips. */
    cites: z.array(z.string()).default([]),
    /**
     * Optional interpretation note — rendered with the INTERP pill above
     * it. Use to flag claims the model derived rather than quoted.
     */
    interp: z.string().optional(),
});
export const Rock = z.object({
    id: z.string().min(1),
    orgId: z.string().min(1),
    /** The single accountable owner. Co-ownership is anti-EOS — exactly one uid. */
    ownerUid: z.string().min(1),
    title: z.string().min(1),
    description: z.string().optional(),
    status: RockStatus,
    /** Quarter tag like `2026-Q2` — drives the quarterly board grouping. */
    quarter: z.string().min(1),
    /** Hard deadline (ISO date) — usually the last day of the quarter. */
    dueDate: z.string(),
    milestones: z.array(RockMilestone).default([]),
    /**
     * Progress fraction 0..1, **server-derived** as
     * `milestones.filter(m => m.completedAt).length / milestones.length`.
     * Stamped on every read so clients render the bar without a second
     * pass; with no milestones, `pct === 0`. Zod-loose (`number` not
     * `min(0).max(1)`) so a stale write from before this field existed
     * still parses; the store layer always recomputes.
     */
    pct: z.number().optional(),
    /** Optional agent-synthesized status update. See `RockAgentUpdate`. */
    agentUpdate: RockAgentUpdate.optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    /** ISO timestamp when status moved to `done`; absent while open. */
    completedAt: z.string().optional(),
});
/**
 * Compute the 0..1 progress fraction from a Rock's milestones.
 *
 * Accepts `undefined` so a legacy Firestore doc that predates the
 * `milestones` field (or a malformed write that drops it) doesn't blow
 * up the entire list endpoint with a `TypeError: Cannot read properties
 * of undefined (reading 'length')`. Treat the missing-array case as
 * "no milestones" → `0`. Same defensive behavior on the client so the
 * `rock.pct ?? computeRockPct(rock.milestones)` fallback never NaNs.
 */
export function computeRockPct(milestones) {
    if (!milestones || milestones.length === 0)
        return 0;
    const done = milestones.filter((m) => typeof m.completedAt === 'string').length;
    return done / milestones.length;
}
/** Wire shape returned by `GET /api/orgs/:o/rocks`. */
export const ListRocksResponse = z.object({
    rocks: z.array(Rock),
});
/** Body for `POST /api/rocks`. */
export const CreateRockRequest = z.object({
    ownerUid: z.string().min(1),
    title: z.string().min(1),
    description: z.string().optional(),
    status: RockStatus.default('on-track'),
    quarter: z.string().min(1),
    dueDate: z.string(),
    milestones: z.array(RockMilestone).default([]),
});
/** Body for `PATCH /api/rocks/:id` — every field is optional. */
export const UpdateRockRequest = z.object({
    ownerUid: z.string().min(1).optional(),
    title: z.string().min(1).optional(),
    description: z.string().nullable().optional(),
    status: RockStatus.optional(),
    quarter: z.string().min(1).optional(),
    dueDate: z.string().optional(),
    milestones: z.array(RockMilestone).optional(),
    completedAt: z.string().nullable().optional(),
});
